import { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import {
  createPortcallStatusLog,
  getPortcallStatusLog,
} from '../../../slices/portCallSlice';
import {
  selectGroupedWaste,
  selectMarpol,
  updateDeliveryWaste,
} from '../slices/marpolSlice';

import AnnexFiveTable from './AnnexFiveTable';
import AnnexFourTable from './AnnexFourTable';
import AnnexOneTable from './AnnexOneTable';
import AnnexSixTable from './AnnexSixTable';
import AnnexTwoTable from './AnnexTwoTable';
import OtherWasteTable from './OtherWasteTable';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import { NotificationAlert } from '../../../../global/alertModal/alertModal';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import PageHeader from '../../../../layout/components/PageHeader';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';
import { validateWasteAnnexData } from '../utils/AnnexDataValidator';
import { NONE_WASTE_DISPOSAL_APPROACH } from '../utils/marpolUtils';
import {
  AnnexesTypesList,
  parseDataForDeliveryWasteEndpoint,
  wasteDisposalApproachOptions,
} from '../utils/waste';
import {
  filterFilledValuesOfWasteItems,
  validationDeliveryWasteSchema,
  validationWasteSchema,
} from '../validators';

import '../styles/marpol.scss';

export const emptyHandleSubmit = async () => {};

function MarpolTables() {
  const formRef = useRef();
  const { formContextRef } = useContext(FormRefContext);
  const firstAnnexIndex = 1;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const marpolData = useSelector(selectMarpol);
  const currentWaste = useSelector(selectGroupedWaste);
  const [selectedAnnex, setSelectedAnnex] = useState(firstAnnexIndex);
  const initialValues = {
    wasteDisposalApproach: marpolData.wasteDisposalApproach,
  };
  const [wasteAnnexDataError, setWasteAnnexDataError] = useState(null);

  const annexOneFormRef = useRef(null);
  const annexTwoFormRef = useRef(null);
  const annexFourFormRef = useRef(null);
  const annexFiveFormRef = useRef(null);
  const annexSixFormRef = useRef(null);
  const otherWasteFormRef = useRef(null);
  const nextUrl = `/${params.uid}/health/first-step`;

  const showFieldsToDischargeWaste = () => {
    return marpolData.wasteToDischarge === null || marpolData.wasteToDischarge;
  };

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [formContextRef, formRef, currentWaste, marpolData]);

  const handleSubmit = async (values) => {
    try {
      const newWaste = parseDataForDeliveryWasteEndpoint({
        currentWaste,
        annexOneFormRef,
        annexTwoFormRef,
        annexFourFormRef,
        annexFiveFormRef,
        annexSixFormRef,
        otherWasteFormRef,
      });

      const filteredNewWaste = filterFilledValuesOfWasteItems(
        marpolData.wasteToDischarge,
        newWaste
      );
      const wasteSchema = validationWasteSchema(
        marpolData.wasteToDischarge,
        filteredNewWaste
      );
      await wasteSchema.validate({}, { abortEarly: false });

      const validationErrors = validateWasteAnnexData(
        newWaste,
        marpolData.wasteToDischarge
      );
      if (validationErrors.length === 0) {
        const isSuccess = await dispatch(
          updateDeliveryWaste({
            marpolData: {
              waste: filteredNewWaste,
              wasteDisposalApproach:
                values.wasteDisposalApproach || NONE_WASTE_DISPOSAL_APPROACH,
            },
            uid: params.uid,
          })
        ).then((response) => {
          if (response.type && response.type.endsWith('/rejected'))
            return false;
          else return true;
        });

        if (!isSuccess) return;

        const isSuccessUpdateStatus = await dispatch(
          createPortcallStatusLog({
            statusLog: {
              uid: params.uid,
              section: DossierSectionOptions.MarpolWaste,
              status: DossierSectionStatusOptions.SubmittedToNmap,
            },
          })
        ).then((response) => {
          if (response.type && response.type.endsWith('/rejected'))
            return false;
          else return true;
        });
        if (!isSuccessUpdateStatus) return;

        dispatch(getPortcallStatusLog(params.uid));

        navigate(nextUrl);
      } else {
        const validationError = validationErrors[0];
        const annex = validationError.annexKey.startsWith('a')
          ? validationError.annexKey.replace('a', 'A')
          : validationError.annexKey.replace('o', 'O');
        setWasteAnnexDataError(
          `[${annex}]:[${validationError.wasteTypeName}]: ${validationError.errorMessage}`
        );
      }
    } catch (validationError) {
      const error_div = document.querySelector('.error.marpol-error');
      error_div.innerHTML = '';
      validationError.inner?.forEach((error) => {
        if (error.message === '* You should fill at least one row.') {
          error_div.innerHTML += `<p style="color: #033272; font-size: 0.9rem;">${error.message}</p><br>`;
        } else {
          error_div.innerHTML += `<p>${error.message}</p><br>`;
        }
      });
    }
  };

  return (
    <div className="select-annexes marpol-tables">
      <PageHeader title={'Marpol waste'} />
      <Formik
        innerRef={formRef}
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationDeliveryWasteSchema(
          marpolData.wasteToDischarge
        )}>
        <Form className="select-annexes-form">
          {showFieldsToDischargeWaste() && (
            <>
              <RadioButtonsCustom
                name="wasteDisposalApproach"
                label="Specify the amount of waste you will dispose of:"
                options={wasteDisposalApproachOptions}
              />
            </>
          )}
          <div className="annex-tabs">
            <div>
              <strong>
                <p>
                  Specify the relevant quantities based on the waste currently
                  onboard, to be discharged, and to be generated at the next
                  port.
                </p>
              </strong>
              <p>
                Fill out only the rows corresponding to the waste onboard, to be
                discharged, or to be generated at the next port, including the
                storage capacity and the next port. If a category does not
                apply, leave the row blank.
              </p>
            </div>
            <div className="annex-tabs-annexes">
              {AnnexesTypesList.map((annex, i) => {
                const className = `annex-tab-item${selectedAnnex === i + 1 ? ' active' : ''}`;
                return (
                  <div
                    className={className}
                    key={`annex-${i}`}
                    onClick={() => {
                      setSelectedAnnex(i + 1);
                    }}>
                    {annex.label}
                  </div>
                );
              })}
            </div>
          </div>
          {
            <AnnexOneTable
              isDisplayed={selectedAnnex === 1}
              formRef={annexOneFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          {
            <AnnexTwoTable
              isDisplayed={selectedAnnex === 2}
              formRef={annexTwoFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          {
            <AnnexFourTable
              isDisplayed={selectedAnnex === 3}
              formRef={annexFourFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          {
            <AnnexFiveTable
              isDisplayed={selectedAnnex === 4}
              formRef={annexFiveFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          {
            <AnnexSixTable
              isDisplayed={selectedAnnex === 5}
              formRef={annexSixFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          {
            <OtherWasteTable
              isDisplayed={selectedAnnex === 6}
              formRef={otherWasteFormRef}
              disableWasteDelivered={!marpolData.wasteToDischarge}
            />
          }
          <div className="error marpol-error"></div>
          <SkipAndNextButtons
            next={nextUrl}
            back={`/${params.uid}/marpol/confirm-waste`}
            submitLabel="Submit"
          />
        </Form>
      </Formik>
      <NotificationAlert
        id={'Alert-Error'}
        alertType={'error'}
        isOpened={wasteAnnexDataError !== null}
        setIsOpened={() => setWasteAnnexDataError(null)}
        title={'There is an error!'}
        message={wasteAnnexDataError}
        showCloseButton={true}
      />
    </div>
  );
}

export default MarpolTables;
